// Client-side video poster (thumbnail) capture.
//
// The pure math below is unit-tested without a browser (see videoPoster.test.ts).
// `capturePosterBlob` is the DOM-bound orchestration around it; it lives here so
// every upload path that accepts video shares ONE implementation — the editor's
// pre-upload dialog and the admin media library both call it. Without a poster
// the backend falls back to using the video URL as the thumbnail, which then
// renders as a broken <img> wherever a thumbnail is shown.

/**
 * Pick the seek timestamp (seconds) for a capture attempt.
 * - attempt 0: 1s in, or the midpoint for very short clips (min(1, dur/2)).
 * - attempt 1+: a quarter of the way in — used to retry a blank/black first frame.
 * Guards against NaN/non-finite/negative durations by clamping to 0.
 */
export function posterTimestamp(duration: number, attempt: number): number {
    const dur = Number.isFinite(duration) && duration > 0 ? duration : 0;
    if (attempt <= 0) {
        return Math.min(1.0, dur / 2);
    }
    return dur * 0.25;
}

/**
 * Mean perceptual luminance (0-255) of an RGBA pixel buffer (as returned by
 * canvas getImageData().data). Alpha is ignored. Empty/odd buffers → 0.
 */
export function meanLuminanceFromRgba(data: ArrayLike<number>): number {
    const pixels = Math.floor(data.length / 4);
    if (pixels <= 0) {
        return 0;
    }
    let sum = 0;
    for (let i = 0; i < pixels; i++) {
        const r = data[i * 4];
        const g = data[i * 4 + 1];
        const b = data[i * 4 + 2];
        sum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }
    return sum / pixels;
}

/**
 * True when a sampled frame is effectively black/blank (mean luminance below
 * threshold) — the signal to retry the capture at a different timestamp.
 */
export function isBlankLuminance(mean: number, threshold = 10): boolean {
    return mean < threshold;
}

/**
 * Target canvas size for the poster: capped at `maxEdge` on the long edge,
 * preserving aspect ratio. Never upscales. Rounds to whole pixels (min 1).
 * Falls back to a 1x1 for degenerate (0/NaN) inputs.
 */
export function posterTargetSize(
    width: number,
    height: number,
    maxEdge = 480
): { width: number; height: number } {
    const w = Number.isFinite(width) && width > 0 ? width : 0;
    const h = Number.isFinite(height) && height > 0 ? height : 0;
    if (w === 0 || h === 0) {
        return { width: 1, height: 1 };
    }
    const longEdge = Math.max(w, h);
    const scale = longEdge > maxEdge ? maxEdge / longEdge : 1;
    return {
        width: Math.max(1, Math.round(w * scale)),
        height: Math.max(1, Math.round(h * scale)),
    };
}

// Capture a poster (JPEG blob) from a local video File, entirely client-side.
// Any failure/timeout resolves to null so the upload is NEVER blocked on it
// (the backend falls back to the video URL as its thumbnail). Hard ~8s budget.
export const capturePosterBlob = (file: File): Promise<Blob | null> => {
    return new Promise<Blob | null>((resolve) => {
        let settled = false;
        const video = document.createElement('video');
        const objectUrl = URL.createObjectURL(file);
        let iosTimer: ReturnType<typeof setTimeout> | null = null;
        let iosRetried = false;
        let blankRetried = false;

        const cleanup = () => {
            clearTimeout(budgetTimer);
            if (iosTimer) clearTimeout(iosTimer);
            video.removeEventListener('loadedmetadata', onLoadedMetadata);
            video.removeEventListener('seeked', onSeeked);
            video.removeEventListener('error', onError);
            try {
                video.pause();
            } catch { /* ignore */ }
            video.removeAttribute('src');
            URL.revokeObjectURL(objectUrl);
        };
        const finish = (blob: Blob | null) => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(blob);
        };
        // Hard overall budget: the upload must never get stuck on poster work.
        const budgetTimer = setTimeout(() => finish(null), 8000);

        const seekTo = (attempt: number) => {
            try {
                video.currentTime = posterTimestamp(video.duration || 0, attempt);
            } catch {
                finish(null);
            }
        };
        // iOS often refuses to seek until the media is playing; if 'seeked'
        // never fires, nudge it with play()+pause() once, then seek again.
        const armIosFallback = () => {
            if (iosTimer) clearTimeout(iosTimer);
            iosTimer = setTimeout(() => {
                if (settled || iosRetried) return;
                iosRetried = true;
                video.play()
                    .then(() => {
                        video.pause();
                        seekTo(0);
                    })
                    .catch(() => finish(null));
            }, 3000);
        };
        const onLoadedMetadata = () => {
            seekTo(0);
            armIosFallback();
        };
        const onSeeked = () => {
            if (iosTimer) {
                clearTimeout(iosTimer);
                iosTimer = null;
            }
            try {
                const target = posterTargetSize(video.videoWidth, video.videoHeight, 480);
                const canvas = document.createElement('canvas');
                canvas.width = target.width;
                canvas.height = target.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    finish(null);
                    return;
                }
                ctx.drawImage(video, 0, 0, target.width, target.height);

                // Black/blank-frame guard: sample a 16x16 downscale. If it's
                // effectively black, retry once at a later timestamp; if it's
                // still blank, skip the thumbnail rather than upload a black one.
                const small = document.createElement('canvas');
                small.width = 16;
                small.height = 16;
                const sctx = small.getContext('2d');
                if (sctx) {
                    sctx.drawImage(canvas, 0, 0, 16, 16);
                    const mean = meanLuminanceFromRgba(sctx.getImageData(0, 0, 16, 16).data);
                    if (isBlankLuminance(mean)) {
                        if (!blankRetried) {
                            blankRetried = true;
                            seekTo(1);
                            return;
                        }
                        finish(null);
                        return;
                    }
                }

                canvas.toBlob((blob) => finish(blob), 'image/jpeg', 0.8);
            } catch (err) {
                // Tainted canvas / HEVC-undecodable / getImageData failure —
                // skip the thumbnail gracefully.
                console.warn('[VIDEO] Poster capture failed, skipping thumbnail', err);
                finish(null);
            }
        };
        const onError = () => finish(null);

        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.addEventListener('loadedmetadata', onLoadedMetadata);
        video.addEventListener('seeked', onSeeked);
        video.addEventListener('error', onError);
        video.src = objectUrl;
    });
};
